/**
 * widgets/templates.tsx — továrny na widgety. Widget se z principu nepíše ručně;
 * poskládá se z pár šablon deklarací (tool + jak vykreslit). Tělo kreslí JEN
 * obsah — Card a povinnou hlavičku vlastní WidgetShell.
 *
 * Tohle je to „rovnou šablonovat": z existujících karet (stat / seznam) se stanou
 * konfigurací generované widgety. Showcase konfigurací patří do modulu `_example`.
 */
import type { WidgetRenderProps, WidgetSpec, WidgetState } from './types';
import { useTranslation } from 'react-i18next';
import i18n from '../lib/i18n';

type IconType = React.ComponentType<{ size?: number | string; className?: string }>;

/**
 * Modul, do kterého widget patří, se ODVOZUJE z prefixu ID (`<modul>.<slug>`) —
 * jediný zdroj pravdy, žádný nesoulad id↔modul. Explicitní `module` (i null)
 * má přednost: null = core widget, nikdy nezašedne.
 */
export function moduleOf(id: string, override?: string | null): string | undefined {
  if (override !== undefined) return override ?? undefined;
  const i = id.indexOf('.');
  return i > 0 ? id.slice(0, i) : undefined;
}

/** Stat karta — jedno číslo + popisek z výsledku toolu. */
export function statWidget<T = Record<string, unknown>>(cfg: {
  id: string;
  title: string;
  /** Override modulu (null = core, nikdy nezašedne). Default = prefix ID. */
  module?: string | null;
  tool: string;
  args?: Record<string, unknown>;
  icon?: IconType;
  /** Vytáhne z výsledku hodnotu + volitelnou jednotku/popisek. */
  pick: (data: T) => { value: string | number; unit?: string; sub?: string };
  refetchInterval?: number;
}): WidgetSpec {
  const Body = ({ data }: WidgetRenderProps) => {
    const { value, unit, sub } = cfg.pick(data as T);
    return (
      <div className='h-full flex flex-col justify-center px-4 py-3'>
        <div className='flex items-baseline gap-1'>
          <span className='text-3xl font-extrabold text-white tabular-nums'>{value}</span>
          {unit && <span className='text-sm text-gray-500'>{unit}</span>}
        </div>
        {sub && <span className='text-xs text-gray-500 mt-1'>{sub}</span>}
      </div>
    );
  };
  return {
    id: cfg.id,
    title: cfg.title,
    module: moduleOf(cfg.id, cfg.module),
    kind: 'stat',
    formats: ['sm', 'md'],
    format: 'sm',
    icon: cfg.icon,
    mcp: { tool: cfg.tool, args: cfg.args },
    refetchInterval: cfg.refetchInterval,
    component: Body,
  };
}

/** Seznam — pole z výsledku toolu, každý řádek přes row(). */
export function listWidget<Item = Record<string, unknown>>(cfg: {
  id: string;
  title: string;
  /** Override modulu (null = core, nikdy nezašedne). Default = prefix ID. */
  module?: string | null;
  tool: string;
  args?: Record<string, unknown>;
  icon?: IconType;
  /** Klíč pole ve výsledku ('nodes', 'containers', …) nebo extractor. */
  items: string | ((data: unknown) => Item[]);
  row: (item: Item, i: number) => React.ReactNode;
  /** Limit řádků na mobilu / desktopu. */
  limit?: { mobile: number; desktop: number };
  emptyLabel?: string;
  format?: 'md' | 'lg';
}): WidgetSpec {
  const getItems = (data: unknown): Item[] => {
    if (typeof cfg.items === 'function') return cfg.items(data) ?? [];
    const arr = (data as Record<string, unknown>)?.[cfg.items];
    return Array.isArray(arr) ? (arr as Item[]) : [];
  };
  const Body = ({ data, viewport }: WidgetRenderProps) => {
    const { t } = useTranslation();
    const items = getItems(data);
    const lim = cfg.limit
      ? (viewport === 'mobile' ? cfg.limit.mobile : cfg.limit.desktop)
      : items.length;
    const shown = items.slice(0, lim);
    return (
      <div className='divide-y divide-gray-800/50'>
        {shown.map((it, i) => (
          <div key={i} className='px-3 py-2 text-sm'>{cfg.row(it, i)}</div>
        ))}
        {items.length > shown.length && (
          <div className='px-3 py-1.5 text-xs text-gray-600'>+{items.length - shown.length} {t('templates.dalsich', 'dalších')}</div>
        )}
      </div>
    );
  };
  return {
    id: cfg.id,
    title: cfg.title,
    module: moduleOf(cfg.id, cfg.module),
    kind: 'list',
    formats: ['md', 'lg'],
    format: cfg.format ?? 'md',
    icon: cfg.icon,
    mcp: { tool: cfg.tool, args: cfg.args },
    // Prázdný seznam = validní „nic tu není", ne chyba.
    interpret: (result): { state: WidgetState; message?: string } => {
      const items = getItems(result);
      return items.length === 0
        ? { state: 'ready', message: cfg.emptyLabel ?? i18n.t('templates.prazdne', 'Prázdné') }
        : { state: 'ready' };
    },
    component: Body,
  };
}
